/**
 * Deterministic event ID generation for client/server deduplication.
 *
 * When both client-side and server-side fire the same event (e.g., purchase),
 * ad platforms (Meta, TikTok, Pinterest, Google Ads) need matching event_id
 * values to deduplicate. This function produces a deterministic UUID from
 * the event name and a unique key (typically an order ID).
 *
 * The formula is: md5(eventName + ':' + key) formatted as UUID (8-4-4-4-12).
 *
 * Server-side implementations (PHP, Node) should use the same formula:
 *   PHP:  md5($eventName . ':' . $key) with sprintf UUID formatting
 *   Node: md5(eventName + ':' + key) with slice UUID formatting
 *
 * @example
 * ```typescript
 * import { deterministicEventId } from '@ownedoperated/unified-event-system/client';
 *
 * // Client-side purchase with deduplication
 * const eventId = deterministicEventId('purchase', orderId);
 * tracker.track('purchase', {
 *   event_id: eventId,
 *   ecommerce: { order_id: orderId, ... },
 * });
 *
 * // Server-side (PHP) produces the same ID:
 * // $eventId = md5('purchase:' . $orderId); // formatted as UUID
 * ```
 */
import { md5 } from './md5';

export function deterministicEventId(eventName: string, key: string): string {
  const hash = md5(eventName + ':' + key);

  return [
    hash.slice(0, 8),
    hash.slice(8, 12),
    hash.slice(12, 16),
    hash.slice(16, 20),
    hash.slice(20, 32),
  ].join('-');
}
